import React from 'react';
import { useTranslation } from 'react-i18next';
import { Select, MenuItem, FormControl, type SelectChangeEvent } from '@mui/material';
import LanguageIcon from '@mui/icons-material/Language';
import { SUPPORTED_LANGUAGES, ensureLocaleLoaded, type SupportedLang } from '../i18n';
import { Flag } from '../utils/flags';

interface LanguageSwitcherProps {
  /**
   * 'icon'  → globe icon only (compact, used inside nav bars)
   * 'label' → flag + locale name (used in footers / settings)
   */
  variant?: 'icon' | 'label';
  /** Any additional sx props forwarded to the FormControl */
  sx?: object;
}

export default function LanguageSwitcher({ variant = 'icon', sx }: LanguageSwitcherProps) {
  const { i18n, t } = useTranslation();

  const currentLang = (
    SUPPORTED_LANGUAGES.find((l) => l.code === i18n.language) ? i18n.language : 'en'
  ) as SupportedLang;

  const handleChange = async (e: SelectChangeEvent<string>) => {
    const lang = e.target.value as SupportedLang;
    // Load the bundle FIRST. If we called changeLanguage before the bundle
    // arrived, react-i18next would rerender once against the empty bundle
    // (falling back to English) and never rerender again once the bundle
    // landed — the UI would appear stuck in English until a page refresh.
    await ensureLocaleLoaded(lang);
    await i18n.changeLanguage(lang);
  };

  return (
    <FormControl
      size="small"
      variant="outlined"
      sx={{
        minWidth: variant === 'icon' ? 56 : 160,
        '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
        '& .MuiSelect-select': {
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          py: '6px',
          pl: variant === 'icon' ? '6px' : '10px',
          fontSize: 13,
          color: 'var(--color-text-secondary)',
        },
        ...sx,
      }}
    >
      <Select
        value={currentLang}
        onChange={handleChange}
        displayEmpty
        aria-label={t('languageSwitcher.label')}
        renderValue={(val) => {
          const lang = SUPPORTED_LANGUAGES.find((l) => l.code === val);
          // Compact 'icon' variant keeps the globe — it's the universal
          // "change language" affordance and the trigger area is small.
          // 'label' variant shows the active locale's flag + name so the
          // current selection is readable at a glance.
          if (variant === 'icon') {
            return <LanguageIcon sx={{ fontSize: 16, flexShrink: 0 }} />;
          }
          return (
            <>
              {lang?.country ? (
                <Flag countryCode={lang.country} size={14} title={lang.label} />
              ) : (
                <LanguageIcon sx={{ fontSize: 16, flexShrink: 0 }} />
              )}
              <span>{lang?.label ?? val}</span>
            </>
          );
        }}
        MenuProps={{
          PaperProps: {
            sx: {
              mt: 0.5,
              minWidth: 180,
              background: 'var(--color-bg-surface)',
              border: '1px solid var(--color-border-default)',
              borderRadius: 'var(--radius-md)',
              boxShadow: '0 8px 32px rgba(0,0,0,0.24)',
            },
          },
        }}
      >
        {SUPPORTED_LANGUAGES.map(({ code, label, country }) => (
          <MenuItem
            key={code}
            value={code}
            selected={code === currentLang}
            sx={{
              fontSize: 13,
              gap: 1.5,
              display: 'flex',
              alignItems: 'center',
              color: 'var(--color-text-primary)',
              '&.Mui-selected': {
                background: 'var(--color-bg-elevated)',
                fontWeight: 600,
              },
              '&:hover': { background: 'var(--color-bg-hover)' },
            }}
          >
            <Flag countryCode={country} size={14} title={label} />
            <span>{label}</span>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}
